import type { ClubAccess } from "@clubcore/api";

/**
 * A club the signed-in user can act in, with its presentation settings, their role and permissions.
 * `/auth/me` returns this for ordinary users; platform staff get it from `GET /club` after choosing.
 */
export type ClubProfile = ClubAccess & { viaPlatformStaff?: boolean };

interface Snapshot {
  slug: string | null;
}

const EMPTY: Snapshot = { slug: null };

/**
 * The club the user is working in. The API client reads `getSlug()` for every request's X-Club
 * header; React reads the snapshot. The choice is remembered per user in localStorage.
 */
export class ClubSelection {
  private snapshot: Snapshot = EMPTY;
  private userId: string | null = null;
  /** Set on a club's own domain: the server decides the club there, so the app follows and never switches away. */
  private pinned: string | null = null;
  private listeners = new Set<() => void>();

  getSlug = (): string | null => this.snapshot.slug;
  getSnapshot = (): Snapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Called after sign-in: restores that user's last club. */
  attach(userId: string): void {
    this.userId = userId;
    let slug: string | null = null;
    try {
      const raw = localStorage.getItem(this.key());
      // Older versions stored a whole profile here too; only the slug is used now.
      if (raw) slug = (JSON.parse(raw) as { slug?: string | null }).slug ?? null;
    } catch {
      /* ignore unreadable storage */
    }
    this.set(this.pinned ? { slug: this.pinned } : slug ? { slug } : EMPTY);
  }

  /**
   * The page is on a club's own domain (the API says so in `X-ClubCore-Host-Club`): that club is the one, whatever was chosen
   * or remembered before. Not stored, so the shared address keeps the user's own choice.
   */
  pin(slug: string): void {
    if (this.pinned === slug) return;
    this.pinned = slug;
    this.set({ slug });
  }

  isPinned(): boolean {
    return this.pinned !== null;
  }

  /** Called on sign-out: forgets the in-memory choice but keeps it stored for next time. */
  detach(): void {
    this.userId = null;
    this.set(EMPTY);
  }

  select(slug: string): void {
    if (this.pinned && slug !== this.pinned) return;
    this.set({ slug });
    if (!this.userId) return;
    try {
      localStorage.setItem(this.key(), JSON.stringify(this.snapshot));
    } catch {
      /* choice lasts for this visit */
    }
  }

  /** Drops a stale choice (club no longer available). */
  forget(): void {
    if (this.pinned) return;
    this.set(EMPTY);
    if (!this.userId) return;
    try {
      localStorage.removeItem(this.key());
    } catch {
      /* nothing stored */
    }
  }

  private key() {
    return `clubcore.club.${this.userId}`;
  }

  private set(next: Snapshot) {
    this.snapshot = next;
    for (const listener of [...this.listeners]) listener();
  }
}
